import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, SECTORS } from "@/data/catalog";
import { Search, Wrench, UtensilsCrossed, Sparkles, ArrowRight, ShieldCheck, MessageCircle, TrendingUp } from "lucide-react";
import BusinessCard from "@/components/BusinessCard";
import SectorPills from "@/components/SectorPills";
import GetQuoteModal from "@/components/GetQuoteModal";
import api from "@/lib/api";

const CAT_ICONS = { home_services: Wrench, food_restaurants: UtensilsCrossed, salons_beauty: Sparkles };

export default function HomePage() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("");
  const [selSector, setSelSector] = useState("");
  const [featured, setFeatured] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    api.get("/businesses", { params: { limit: 8 } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const doSearch = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (sector) params.set("sector", sector);
    nav(`/search?${params.toString()}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative gradient-emerald-hero overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 sm:pt-16 sm:pb-20 lg:pt-20 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-emerald-200 text-emerald-800 text-[11px] sm:text-xs font-semibold px-3 py-1.5 rounded-full mb-4 sm:mb-5">
                <ShieldCheck className="w-3.5 h-3.5" /> Islamabad's verified local directory
              </div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1]" style={{ fontFamily: "Outfit" }}>
                Find trusted <span className="text-emerald-800">local businesses</span> in Islamabad.
              </h1>
              <p className="mt-4 sm:mt-5 text-base sm:text-lg text-slate-600 max-w-xl">
                Verified electricians, restaurants, salons — one WhatsApp tap away.
              </p>

              <div className="mt-6 sm:mt-8 bg-white rounded-2xl border border-slate-200 shadow-lg p-2 flex flex-col sm:flex-row gap-2">
                <div className="flex-1 flex items-center px-3">
                  <Search className="w-5 h-5 text-slate-400 mr-2" />
                  <Input
                    data-testid="hero-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                    placeholder='Try "AC repair" or "biryani"'
                    className="border-0 shadow-none focus-visible:ring-0 text-base h-11"
                  />
                </div>
                <div className="w-full sm:w-52">
                  <select value={sector} onChange={(e) => setSector(e.target.value)} data-testid="hero-sector-select" className="h-11 w-full rounded-md border-0 bg-slate-50 px-3 text-sm appearance-none bg-no-repeat" style={{ backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b' stroke-width='2'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3e%3c/svg%3e\")", backgroundPosition: "right 0.6rem center", backgroundSize: "1.1rem", paddingRight: "2.2rem" }}>
                    <option value="">Any sector</option>
                    {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <Button onClick={doSearch} data-testid="hero-search-btn" className="h-11 px-6 bg-emerald-800 hover:bg-emerald-900 text-white font-semibold">Search</Button>
              </div>

              <div className="mt-3 sm:mt-4 flex flex-wrap gap-x-3 gap-y-1 text-xs sm:text-sm text-slate-500">
                <span className="font-medium text-slate-700">Popular:</span>
                {["Electrician F-7", "Cafe Blue Area", "AC Repair G-11", "Salon DHA"].map((t) => (
                  <button key={t} onClick={() => { setQ(t); }} className="hover:text-emerald-700 underline underline-offset-2">{t}</button>
                ))}
              </div>

              <div className="mt-5 sm:mt-8 flex flex-wrap gap-x-4 gap-y-2 text-xs sm:text-sm text-slate-600">
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-700" /> Phone verified</div>
                <div className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp chat</div>
                <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-amber-600" /> Real reviews</div>
              </div>
            </div>

            {/* Bento cards */}
            <div className="lg:col-span-5 grid grid-cols-2 gap-3 sm:gap-4">
              {CATEGORIES.map((c, i) => {
                const Icon = CAT_ICONS[c.key];
                return (
                  <button
                    key={c.key}
                    onClick={() => nav(`/search?category=${c.key}`)}
                    data-testid={`hero-category-${c.key}`}
                    className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-white text-left card-hover ${i === 0 ? "col-span-2 aspect-[16/9] sm:aspect-[16/9]" : "aspect-[4/5] sm:aspect-square"}`}
                  >
                    <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                    <div className="absolute inset-0 p-4 flex flex-col justify-between">
                      <div className="w-9 h-9 bg-white/95 backdrop-blur rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-emerald-800" />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-emerald-200 font-bold">{c.tag}</div>
                        <div className="text-white font-bold text-lg flex items-center gap-1 mt-0.5" style={{ fontFamily: "Outfit" }}>
                          {c.name} <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SECTOR NAV */}
      <section className="border-y border-slate-200 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 py-2 min-w-0">
            <div className="hidden sm:block text-xs font-bold uppercase tracking-widest text-emerald-800 whitespace-nowrap flex-shrink-0">Browse by sector</div>
            <div className="flex-1 min-w-0">
              <SectorPills value={selSector} onChange={(s) => { setSelSector(s); nav(`/search?sector=${encodeURIComponent(s)}`); }} />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="flex items-end justify-between mb-4 sm:mb-6 gap-2">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">Just added</div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Outfit" }}>Fresh in the directory</h2>
          </div>
          <Button variant="ghost" onClick={() => nav("/search")} data-testid="see-all-businesses-btn" className="text-emerald-800 font-semibold">See all <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </div>
        {featured.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 p-10 text-center">
            <div className="text-emerald-800 font-bold text-lg" style={{ fontFamily: "Outfit" }}>No businesses yet — be the first!</div>
            <p className="text-slate-600 mt-1">Add your business or claim an existing listing to get discovered.</p>
            <Button onClick={() => nav("/login?mode=signup")} className="mt-4 bg-emerald-800 hover:bg-emerald-900" data-testid="empty-list-cta">List your business</Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {featured.map((b) => <BusinessCard key={b.business_id} b={b} />)}
          </div>
        )}
      </section>

      {/* GET QUOTE CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16">
        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-700 p-6 sm:p-12">
          <div className="absolute inset-0 grain-overlay opacity-30" />
          <div className="relative grid md:grid-cols-2 gap-6 md:gap-8 items-center">
            <div>
              <div className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-emerald-200">Not sure who to call?</div>
              <h3 className="text-2xl sm:text-4xl font-extrabold text-white mt-2 leading-tight" style={{ fontFamily: "Outfit" }}>
                Get 3 free quotes in minutes.
              </h3>
              <p className="text-emerald-100 mt-3 max-w-md text-sm sm:text-base">Tell us what you need — we'll route your request to the top matching verified pros.</p>
            </div>
            <div className="flex md:justify-end">
              <Button onClick={() => setQuoteOpen(true)} size="lg" data-testid="hero-get-quote-btn" className="bg-white text-emerald-900 hover:bg-emerald-50 font-bold text-base h-12 px-6 sm:px-8 w-full md:w-auto">
                Get free quotes <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <GetQuoteModal open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
